// THIS IS AN AUTOGENERATED FILE. DO NOT EDIT THIS FILE DIRECTLY.

import { useMutation, useMutationState } from '@tanstack/react-query';
import { useHttpClient } from '@toggl/sync/http';
import type {
  SubscriptionsFinishTrialRequest,
  SubscriptionsStartTrialRequest,
} from '../types';
import type { MutationOptions } from '../utils/mutations';
import { postFinishTrialMutationKey, postStartTrialMutationKey } from './keys';
import { postFinishTrial, postStartTrial } from './requests';

export type PostStartTrialMutationVariables = {
  workspace_id: number;
  data: SubscriptionsStartTrialRequest;
};

export type PostStartTrialMutationOptions<TContext = unknown> = MutationOptions<
  typeof postStartTrial,
  PostStartTrialMutationVariables,
  TContext
>;

/**
 * Starts a workspace trial
 */
export function usePostStartTrialMutation<TContext = unknown>(
  options: PostStartTrialMutationOptions<TContext> = {}
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ workspace_id, data }) =>
      postStartTrial(httpClient, workspace_id, data),
    mutationKey: postStartTrialMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending postStartTrial mutation. */
export function usePostStartTrialMutationVariables() {
  return useMutationState({
    filters: { mutationKey: postStartTrialMutationKey, status: 'pending' },
    select: (mutation) =>
      mutation.state.variables as PostStartTrialMutationVariables,
  });
}

export type PostFinishTrialMutationVariables = {
  workspace_id: number;
  trial_id: number;
  data: SubscriptionsFinishTrialRequest;
};

export type PostFinishTrialMutationOptions<TContext = unknown> =
  MutationOptions<
    typeof postFinishTrial,
    PostFinishTrialMutationVariables,
    TContext
  >;

/**
 * Allows support to finish a trial prematurely
 */
export function usePostFinishTrialMutation<TContext = unknown>(
  options: PostFinishTrialMutationOptions<TContext> = {}
) {
  const httpClient = useHttpClient();
  return useMutation({
    mutationFn: ({ workspace_id, trial_id, data }) =>
      postFinishTrial(httpClient, workspace_id, trial_id, data),
    mutationKey: postFinishTrialMutationKey,
    ...options,
  });
}

/** Returns the variables of a pending postFinishTrial mutation. */
export function usePostFinishTrialMutationVariables() {
  return useMutationState({
    filters: { mutationKey: postFinishTrialMutationKey, status: 'pending' },
    select: (mutation) =>
      mutation.state.variables as PostFinishTrialMutationVariables,
  });
}
